import React, { createContext, useContext, useEffect, useState, ReactNode, Suspense, lazy } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';
import { SocketProvider } from './contexts/SocketContext';
import { NotificationProvider } from './contexts/NotificationContext';

import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';

import Header from './components/Header';
import Footer from './components/Footer';
import MobileTabBar from './components/MobileTabBar';
import DocumentI18nSync from './components/DocumentI18nSync';
import { isNativeApp } from './utils/runtimeEnvironment';
import { handleCloseApp, shouldShowCloseAppButton } from './utils/closeApp';
import { Power } from 'lucide-react';

// Critical path: تحميل فوري للصفحة الرئيسية وتسجيل الدخول
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import AdminLoginPage from './pages/AdminLoginPage';

// Code splitting: تحميل كسول لتقليل حجم main.js وتحسين FCP
const RegisterPage = lazy(() => import('./pages/Register'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ArchivePage = lazy(() => import('./pages/ArchivePage'));
const AuctionDetailPage = lazy(() => import('./pages/AuctionDetailPage'));
const CreateAuctionPage = lazy(() => import('./pages/CreateAuctionPage'));
const EditAuctionPage = lazy(() => import('./pages/EditAuctionPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminManagementPage = lazy(() => import('./pages/AdminManagementPage'));
const BannerManagement = lazy(() => import('./pages/BannerManagement'));
const AnnouncementDetailsPage = lazy(() => import('./pages/AnnouncementDetailsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AuctionsPage = lazy(() => import('./pages/AuctionsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));

const PageLoader: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <div
      className="min-h-[50vh] flex items-center justify-center bg-gray-50 dark:bg-slate-900 transition-opacity duration-150"
      role="status"
      aria-label={t('loader')}
    >
      <p className="text-slate-200">{t('loader')}</p>
    </div>
  );
};

// Dark Mode Context
interface DarkModeContextType {
  darkMode: boolean;
  toggleDarkMode: () => void;
}
const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);
export const useDarkMode = () => {
  const ctx = useContext(DarkModeContext);
  if (!ctx) throw new Error('useDarkMode must be used within DarkModeProvider');
  return ctx;
};
const DarkModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    return stored === 'true';
  });
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode ? 'true' : 'false');
  }, [darkMode]);
  const toggleDarkMode = () => setDarkMode((d) => !d);
  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};

function App() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const hideFooter = location.pathname === '/messages';
  // Cache the native-app check once (it won't change within a session)
  const nativeMode = React.useMemo(() => isNativeApp(), []);
  const [showCloseApp, setShowCloseApp] = useState(false);

  useEffect(() => {
    const updateCloseAppVisibility = () => setShowCloseApp(shouldShowCloseAppButton());
    updateCloseAppVisibility();
    window.addEventListener('resize', updateCloseAppVisibility);
    return () => window.removeEventListener('resize', updateCloseAppVisibility);
  }, []);

  return (
    <DarkModeProvider>
      <AuthProvider>
        <SiteSettingsProvider>
          <SocketProvider>
            <NotificationProvider>
            <div className="page-wrapper min-h-screen bg-transparent dark:bg-slate-900 flex flex-col w-full max-w-[100vw] overflow-x-clip">
              <DocumentI18nSync />
              {!nativeMode && <Header />}
              <main className={`flex-1 w-full min-w-0${nativeMode ? ' native-app-content' : ''}${nativeMode && showCloseApp ? ' pb-14' : ''}`}>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/admin-login" element={<AdminLoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                    <Route path="/auctions" element={<AuctionsPage />} />
                    <Route path="/announcement-details" element={<AnnouncementDetailsPage />} />
                    <Route path="/archive" element={
                      <PrivateRoute>
                        <ArchivePage />
                      </PrivateRoute>
                    } />
                    <Route path="/auction/:id" element={<AuctionDetailPage />} />
                    <Route path="/create-auction" element={
                      <PrivateRoute>
                        <CreateAuctionPage />
                      </PrivateRoute>
                    } />
                    <Route path="/edit-auction/:id" element={
                      <PrivateRoute>
                        <EditAuctionPage />
                      </PrivateRoute>
                    } />
                    <Route path="/profile" element={
                      <PrivateRoute>
                        <ProfilePage />
                      </PrivateRoute>
                    } />
                    <Route path="/profile/:id" element={<ProfilePage />} />
                    <Route path="/messages" element={
                      <PrivateRoute>
                        <MessagesPage />
                      </PrivateRoute>
                    } />
                    <Route path="/notifications" element={
                      <PrivateRoute>
                        <NotificationsPage />
                      </PrivateRoute>
                    } />
                    <Route path="/admin" element={
                      <AdminRoute>
                        <AdminPage />
                      </AdminRoute>
                    } />
                    <Route path="/admin/management" element={
                      <AdminRoute>
                        <AdminManagementPage />
                      </AdminRoute>
                    } />
                    <Route path="/banner-management" element={
                      <AdminRoute>
                        <BannerManagement />
                      </AdminRoute>
                    } />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </main>
              {!nativeMode && !hideFooter && <Footer />}
              {!nativeMode && <MobileTabBar />}
              {nativeMode && showCloseApp && (
                <div className="native-close-app-bar fixed bottom-0 inset-x-0 z-50 md:hidden border-t border-slate-600 bg-slate-800/95 backdrop-blur-sm safe-area-pb">
                  <button
                    type="button"
                    onClick={handleCloseApp}
                    className="flex w-full items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-400 hover:bg-red-900/20 active:bg-red-900/30 transition-colors"
                  >
                    <Power className="h-4 w-4 shrink-0" />
                    <span>{t('nav.closeApp')}</span>
                  </button>
                </div>
              )}
            </div>
            </NotificationProvider>
          </SocketProvider>
        </SiteSettingsProvider>
      </AuthProvider>
    </DarkModeProvider>
  );
}

export default App;